import {
  useMutation,
  useQuery,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";
import { useState, useMemo, useCallback, useEffect } from "react";
import { useDebounce } from "use-debounce";
import CollaboratorsHistoryLanguagesService from "../services/api/collaborators/CollaboratorsHistoryLanguage.service";
import {
  COLLABORATORS_PAGE_SIZE,
  RefreshReactQuery,
  TECH_SKILLS_PAGE_SIZE,
} from "../constants/AppConstants";
import {
  AddCollaboratorsHistorySkillLanguages,
  AddCollaboratorsHistoryTechSkillRequestList,
  CollaboratorsHistorySkillLanguages,
  CollaboratorsHistoryTechSkill,
  UpdateCollaboratorsHistorySkillLanguages,
  UpdateCollaboratorsHistoryTechSkillRequest,
} from "../types/api/collaborators.type";
import CollaboratorsHistoryTechSkillsService from "../services/api/collaborators/CollaboratorHistoryTechSkills.services";
import TechSkillsService from "../services/api/techSkills/TechSkills.service";
import { TechSkillPropertyEnum } from "../types/AppTypes";
import {
  DATA_ADDED_SUCCESS_LABEL,
  DATA_DELETED_SUCCESS_LABEL,
  DATA_UPDATED_SUCCESS_LABEL,
  ERROR_LABEL,
  ERROR_MESSAGE_LABEL,
} from "../constants/Dictionary";
import { useNotification } from "./notification.hook";

export const useHistoryLanguage = (profileId: number) => {
  const { notification } = useNotification();

  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: [`get-collaborator-history-languages-${profileId}`],
    queryFn: () => CollaboratorsHistoryLanguagesService.get(profileId),
    staleTime: RefreshReactQuery.MODERATE,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading, isFetching };
};

export const useHistoryLanguageDelete = (profileId: number) => {
  const queryClient = useQueryClient();
  const { notification } = useNotification();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (selectedLanguageId: number) => {
      return CollaboratorsHistoryLanguagesService.delete(
        profileId,
        selectedLanguageId,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`get-collaborator-history-languages-${profileId}`],
      });
      notification.success(DATA_DELETED_SUCCESS_LABEL, undefined, "toast");
    },
    onError: (error) => {
      console.error(`Error deleting language: ${error}`);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });

  return { mutateAsync, isPending };
};

export const useHistoryLanguageAdd = (profileId: number) => {
  const queryClient = useQueryClient();
  const { notification } = useNotification();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (body: AddCollaboratorsHistorySkillLanguages) => {
      return CollaboratorsHistoryLanguagesService.addLanguage(profileId, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`get-collaborator-history-languages-${profileId}`],
      });
      notification.success(DATA_ADDED_SUCCESS_LABEL, undefined, "toast");
    },
    onError: (error) => {
      console.error(`Error adding language: ${error}`);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });

  return { mutateAsync, isPending };
};

export const useHistoryLanguageUpdate = (profileId: number) => {
  const queryClient = useQueryClient();
  const { notification } = useNotification();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({
      updateBody,
      record,
    }: {
      updateBody: UpdateCollaboratorsHistorySkillLanguages;
      record: CollaboratorsHistorySkillLanguages;
    }) => {
      return CollaboratorsHistoryLanguagesService.update(
        profileId,
        record.id,
        updateBody,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`get-collaborator-history-languages-${profileId}`],
      });
      notification.success(DATA_UPDATED_SUCCESS_LABEL, undefined, "toast");
    },
    onError: (error) => {
      console.error(`Error updating language: ${error}`);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });

  return { mutateAsync, isPending };
};

interface useGetTechSkillsProps {
  profileId: number;
  pageNumber: number;
  searchDebouncedValue: string;
}

export const useHistoryTechSkills = ({
  profileId,
  pageNumber,
  searchDebouncedValue,
}: useGetTechSkillsProps) => {
  const { notification } = useNotification();

  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: [
      `get-collaborator-history-tech-skills-${profileId}`,
      pageNumber,
      searchDebouncedValue,
    ],
    queryFn: ({ signal }) =>
      CollaboratorsHistoryTechSkillsService.list(
        profileId,
        COLLABORATORS_PAGE_SIZE,
        pageNumber,
        [
          ...(searchDebouncedValue
            ? [{ key: "search", value: [searchDebouncedValue] }]
            : []),
        ],
        signal,
      ),
    staleTime: RefreshReactQuery.MODERATE,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading, isFetching };
};

export const useHistoryTechSkillsDelete = (profileId: number) => {
  const queryClient = useQueryClient();
  const { notification } = useNotification();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (selectedRecordId: number) => {
      return CollaboratorsHistoryTechSkillsService.delete(
        profileId,
        selectedRecordId,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`get-collaborator-history-tech-skills-${profileId}`],
      });
      notification.success(DATA_DELETED_SUCCESS_LABEL, undefined, "toast");
    },
    onError: (error) => {
      console.error(`Error deleting technical skills: ${error}`);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });

  return { mutateAsync, isPending };
};

export const useHistoryTechSkillsAdd = (profileId: number) => {
  const queryClient = useQueryClient();
  const { notification } = useNotification();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (
      AddHistoryTech: AddCollaboratorsHistoryTechSkillRequestList,
    ) => {
      return CollaboratorsHistoryTechSkillsService.add(
        profileId,
        AddHistoryTech,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`get-collaborator-history-tech-skills-${profileId}`],
      });
      notification.success(DATA_ADDED_SUCCESS_LABEL, undefined, "toast");
    },
    onError: (error) => {
      console.error(`Error adding technical skills: ${error}`);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });

  return { mutateAsync, isPending };
};

export const useHistoryTechSkillsUpdate = (profileId: number) => {
  const { notification } = useNotification();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({
      body,
      record,
    }: {
      body: UpdateCollaboratorsHistoryTechSkillRequest;
      record: CollaboratorsHistoryTechSkill;
    }) => {
      return CollaboratorsHistoryTechSkillsService.update(
        profileId,
        record.id,
        body,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`get-collaborator-history-tech-skills-${profileId}`],
      });
      notification.success(DATA_UPDATED_SUCCESS_LABEL, undefined, "toast");
    },
    onError: (error) => {
      console.error(`error uploading tech skills ${error}`);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });

  return { mutateAsync, isPending };
};

interface useTechSkillPropertyInfiniteProps {
  property: TechSkillPropertyEnum;
  queryKey: string;
  enabled?: boolean;
  debounce?: number;
}

const useTechSkillPropertyInfinite = ({
  property,
  queryKey,
  enabled = true,
  debounce: debounceMs = 500,
}: useTechSkillPropertyInfiniteProps) => {
  const { notification } = useNotification();
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, debounceMs);

  const {
    data,
    isLoading,
    isFetching,
    hasNextPage,
    fetchNextPage,
    refetch,
    isError,
  } = useInfiniteQuery({
    queryKey: [queryKey, debouncedSearch],
    queryFn: ({ pageParam, signal }) =>
      TechSkillsService.getPropertyList(
        property,
        TECH_SKILLS_PAGE_SIZE,
        pageParam as number,
        [
          ...(debouncedSearch
            ? [{ key: "search", value: [debouncedSearch] }]
            : []),
        ],
        signal,
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      allPages.length * TECH_SKILLS_PAGE_SIZE < lastPage.pagination.total
        ? allPages.length
        : undefined,
    staleTime: RefreshReactQuery.MODERATE,
    enabled,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  const items = useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data],
  );
  const pagination = data?.pages[data.pages.length - 1]?.pagination ?? null;

  const initLoading = isLoading;
  const fetchingMore = isFetching && (data?.pages.length ?? 0) > 0;

  const clearSearch = useCallback(() => setSearch(""), []);

  const fetchMore = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  return {
    data: items,
    initLoading,
    fetchingMore,
    canLoadMore: hasNextPage ?? false,
    fetchMore,
    refetch,
    pagination,
    search,
    setSearch,
    clearSearch,
    page: (data?.pages.length ?? 1) - 1,
  };
};

export const useGetAvailableTechSkills = ({
  searchDebouncedValue,
  pageNumber,
  profileId,
}: useGetTechSkillsProps) => {
  const { notification } = useNotification();

  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: [
      `get-collaborator-history-tech-skills-available-${profileId}`,
      pageNumber,
      searchDebouncedValue,
    ],
    queryFn: ({ signal }) =>
      CollaboratorsHistoryTechSkillsService.getTechSkillsNotAssignedToCollaborator(
        profileId,
        TECH_SKILLS_PAGE_SIZE,
        pageNumber,
        [
          ...(searchDebouncedValue
            ? [{ key: "search", value: [searchDebouncedValue] }]
            : []),
        ],
        signal,
      ),
    staleTime: RefreshReactQuery.MODERATE,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading, isFetching };
};

interface useTechSkillPropertyInfiniteOptions {
  enabled?: boolean;
  debounce?: number;
}

export const useCompetencesInfinite = (
  options: useTechSkillPropertyInfiniteOptions = {},
) =>
  useTechSkillPropertyInfinite({
    property: TechSkillPropertyEnum.COMPETENCE,
    queryKey: "get-infinite-competences",
    ...options,
  });

export const useStreamsInfinite = (
  options: useTechSkillPropertyInfiniteOptions = {},
) =>
  useTechSkillPropertyInfinite({
    property: TechSkillPropertyEnum.STREAM,
    queryKey: "get-infinite-streams",
    ...options,
  });

export const useSystemSoftwaresInfinite = (
  options: useTechSkillPropertyInfiniteOptions = {},
) =>
  useTechSkillPropertyInfinite({
    property: TechSkillPropertyEnum.SOFTWARE,
    queryKey: "get-infinite-system-softwares",
    ...options,
  });

export const useComponentsInfinite = (
  options: useTechSkillPropertyInfiniteOptions = {},
) =>
  useTechSkillPropertyInfinite({
    property: TechSkillPropertyEnum.COMPONENT,
    queryKey: "get-infinite-components",
    ...options,
  });
